import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { Editor as TinymceEditor } from 'tinymce';

import type { Service } from 'webapp-types';
import { BillingFeature, BlockVisualization } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';

import type BlockContentTextComponent from 'webapp/components/block/content/text';
import type BlockSettingsTextComponent from 'webapp/components/block/settings/text';

import VisualizationBase from 'webapp/models-custom/visualization/base';

type EditorComponent = BlockSettingsTextComponent | BlockContentTextComponent;

const RecordLimit = 0;

export default class VisualizationText extends VisualizationBase {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;

	visualizationType = BlockVisualization.TEXT;

	recordLimit = RecordLimit;

	get shownText() {
		return this.block.text;
	}

	getEditorOptionsForComponent(component: EditorComponent) {
		return {
			toolbar: [
				{ name: 't1', items: ['fontselect', 'styleselect'] },
				{ name: 't2', items: ['forecolor', 'bold', 'italic', 'underline'] },
				{ name: 't3', items: ['alignleft', 'aligncenter', 'alignright'] },
				{ name: 't4', items: ['bullist', 'numlist', 'outdent', 'indent'] },
				{ name: 't5', items: ['table', 'emoticons', 'removeformat', 'link', 'hr'] },
				{ name: 'toolbar__group--storybutton', items: ['storybutton'] },
			],
			placeholder: 'Click to add text...',
			onSetup: (__smth: unknown, editor: TinymceEditor) => {
				// syncing between editor on block content and designer right panel
				// on any editor change, we trigger event-manager event
				// components rendering the editors react and update their editor texts
				component.editor = editor;

				const syncChangesFn = () => {
					const content = editor.getContent();
					const id = editor.getElement().closest('.block')?.id.replace('b', '');

					component.eventManagerService.trigger(AppEvent.VIS_TEXT_EDITED, {
						id,
						content,
					});
				};
				editor.on('keyup', syncChangesFn); // for typing
				editor.on('change', syncChangesFn); // for changing styles etc

				editor.ui.registry.addButton('storybutton', {
					text: 'Switch to Data story',
					onAction: () => {
						if (
							this.billingService.checkIfCan(
								BillingFeature.VISUALIZATIONS_ADVANCED,
								true,
							)
						) {
							this.popupService.openConfirmPopup({
								onConfirm: () => {
									void this.blockSetup.changeVisualization(
										BlockVisualization.DATA_STORY,
									);
								},
								title: 'Switch to Data story?',
								text: 'To use data elements, you need to switch to Data story visualization. After switching, select a position and then press <strong>Add Data story</strong> in the toolbar.',
								isWarning: true,
								confirmAction: 'Switch',
								cancelAction: 'Cancel',
							});
						}
					},
				});
			},
		};
	}

	@cached
	get shownDate() {
		const text = '';
		// const date = this.firstDataSelect?.dataRoot?.outputs[0].point?.date;
		// if (date) {
		// 	text = getFormattedTime(date, this.currentUserService.model?.timezone, 'MMM D h:mm A');
		// }
		return text;
	}

	static override recordLimit = RecordLimit;
}
